/**
 * Copy de cara al cliente del checkout, centralizado en UN único archivo para
 * permitir tests verbatim (si alguien edita un texto, el test cae). Incluye las
 * metas de los métodos de pago, el toggle/selector de apartado, el copy de los
 * diálogos y los datos de pago manuales (SINPE/transferencia + WhatsApp).
 *
 * Regla: ningún texto de cara al cliente debe vivir hardcoded en plantillas o
 * componentes — todo pasa por aquí.
 */

/** Métodos de pago REALES que ofrece el checkout (orden de render incluido). */
export type CheckoutPaymentMethod = 'sinpe' | 'transfer' | 'online';

export interface PaymentOptionCopy {
  readonly key: CheckoutPaymentMethod;
  /** Contiene markup `<strong>`; se renderiza con `[innerHTML]`. */
  readonly title: string;
  readonly meta: string;
}

/** Datos de pago manuales (verbatim — bloqueado para tests). */
export const PAYMENT_DETAILS = {
  /** Titular del SINPE Móvil y de la cuenta bancaria (se muestra como «A nombre de»). */
  beneficiary: 'Omar Fernández Zúñiga',
  sinpe: '8912-5220',
  transferBank: 'Banco BAC Credomatic',
  transferAccount: 'CR59010200009506738891',
  whatsapp: '8912-5220',
} as const;

/**
 * Copy COMPARTIDO del método «Mensajero Perrón Store» (ps_delivery), idéntico en checkout y en el
 * modal de precompra (una sola fuente para no divergir). El precio VARÍA por provincia y se formatea
 * aparte con `formatCrc`/`CrcPipe` (con «Desde» cuando aún no hay provincia cubierta elegida).
 * `dialog` es el aviso bloqueante de cambio automático a Correos cuando el cantón no está cubierto;
 * `switchedNote`/`switchedAck` es el snackbar de refuerzo que aparece TRAS aceptar ese cambio.
 * `destinationsLink`/`destinations` alimentan el modal informativo de destinos. Verbatim — bloqueado con test.
 */
export const PS_DELIVERY_COPY = {
  label: 'Mensajero Perrón Store',
  coverageNote:
    'Cobertura limitada a cantones seleccionados de San José, Alajuela, Cartago y Heredia.',
  disabledNote: 'No disponible para tu cantón.',
  dialog: {
    title: 'Cambiamos tu método de envío',
    body: 'El mensajero de Perrón Store no llega a tu cantón. Te cambiamos el envío a Correos de Costa Rica para que tu pedido llegue igual.',
    cta: 'Entendido',
  },
  /** Snackbar de refuerzo tras el cambio a Correos (se auto-oculta; «Entendido» solo lo cierra antes). */
  switchedNote:
    'Cambiamos tu envío a Correos de Costa Rica: el Mensajero Perrón Store no llega a tu cantón.',
  switchedAck: 'Entendido',
  /** Texto del enlace/acción que abre el modal de destinos (junto a la opción y en el snackbar). */
  destinationsLink: 'Ver destinos disponibles',
  /** Modal informativo con las provincias cubiertas, su tarifa y sus cantones. */
  destinations: {
    title: 'Destinos del Mensajero Perrón Store',
    intro: 'Llegamos a estos cantones. La tarifa depende de la provincia:',
    cta: 'Cerrar',
  },
} as const;

export const CHECKOUT_COPY = {
  /**
   * Métodos de pago DIRECTOS, siempre visibles, en orden de render. El apartado
   * NO está aquí: es una modalidad aparte (toggle + selector del 30%).
   */
  paymentOptions: [
    {
      key: 'sinpe',
      title: 'Pagar por <strong>SINPE Móvil</strong>',
      meta: 'Te mostramos los datos para pagar al confirmar el pedido',
    },
    {
      key: 'transfer',
      title: 'Pagar por <strong>transferencia o depósito</strong>',
      meta: 'Te mostramos los datos bancarios al confirmar el pedido',
    },
    {
      key: 'online',
      title: 'Pagar con <strong>tarjeta</strong> de crédito o débito',
      meta: 'Pago en línea seguro',
    },
  ] as readonly PaymentOptionCopy[],

  /** Control de modalidad «Apartar con abonos», independiente de los métodos. */
  layawayToggle: {
    title: 'Apartar con abonos',
    /** Lleva el placeholder `{deposit}` (ver `withDeposit`). */
    meta: 'Abono inicial del 30%: ₡{deposit} — un mes para completar el pago',
    /**
     * Bajo el umbral mínimo (D1/D2, docs/specs/checkout-apartados-umbral-plan.md): el toggle se
     * muestra DESHABILITADO, no oculto, con esta pista. Lleva el placeholder `{minSubtotal}`
     * (ver `withMinSubtotal`).
     */
    disabledHint: 'Disponible en compras desde ₡{minSubtotal}',
  },

  /**
   * Selector del método del abono inicial (30%), tras aceptar el consentimiento.
   * Las OPCIONES reutilizan `paymentOptions` (misma fuente que los métodos directos):
   * título y subtítulo idénticos al flujo normal. Aquí solo vive el encabezado.
   */
  layawayDepositSelector: {
    title: '¿Cómo quieres pagar el abono inicial?',
  },

  /**
   * Modal bloqueante de consentimiento al activar «Apartar con abonos».
   *
   * ⚠️ El `body` está ALINEADO VERBATIM con `docs/legal/terminos-y-condiciones.md` §7.4–7.5, que es
   * el documento legal publicado y la fuente de verdad: (a) la cancelación NO es automática — la
   * tienda contacta al cliente y la anulación es una decisión MANUAL del admin (la API solo envía
   * el correo `LayawayExpired`, ver `preorders:notify-expired-layaways`); (b) solo el enganche del
   * 30 % es no reembolsable, el excedente se reintegra. NO prometer aquí una pérdida total de los
   * abonos ni una cancelación automática: contradiría los T&C y expondría a un reclamo (Ley 7472).
   * Si cambia la política, se cambian PRIMERO los T&C (.md de la web + .docx de Omar) y después
   * este copy y su test verbatim en `checkout-copy.spec.ts`.
   */
  layawayNotice: {
    title: 'Apartar con abonos',
    body: 'Cuando apartas con abonos, tienes un mes para completar el pago de tu pedido. Si superado el plazo el pedido no se ha pagado por completo, nos pondremos en contacto contigo: el abono inicial del 30% no es reembolsable y te reintegramos cualquier monto que hayas abonado por encima. ¡Gracias por tu confianza! 🐾',
    /** Aviso destacado: un apartado queda fijado al crearse (bloque diferenciado). */
    emphasis:
      'Importante: los apartados no son modificables. Una vez creado el pedido, no se pueden cambiar los productos ni la cantidad de unidades.',
    ack: 'He leído y entiendo',
    cta: 'Continuar',
    mascot: '/images/logo/info.png',
  },

  /**
   * Modal bloqueante al elegir SINPE/transferencia: advierte que un comprobante
   * que no corresponda al pago real anula el pedido. La mascota va a la DERECHA
   * (efecto «leyendo» el aviso) y se muestra una sola vez (localStorage).
   */
  proofNotice: {
    title: 'Revisa tu comprobante de pago',
    body: 'Los pedidos con un comprobante de pago que no corresponda al pago real del mismo, serán anulados automáticamente por el sistema. Es importante revisar el comprobante antes de adjuntarlo. ¡Gracias! :)',
    ack: 'He leído y entiendo',
    cta: 'Entendido',
    mascot: '/images/logo/reading_at_left.png',
  },

  /**
   * Modal de datos de pago + comprobante (SINPE / transferencia). El comprobante
   * es OBLIGATORIO: el pedido se crea al pulsar «Finalizar» con la imagen ya
   * adjunta (create-on-finalize). Sin imagen no hay pedido.
   */
  paymentData: {
    intro: 'Revisa los datos y adjunta tu comprobante para finalizar.',
    sinpe: { title: 'Completa tu pago por SINPE Móvil' },
    transfer: { title: 'Completa tu pago por transferencia o depósito' },
    totalLabel: 'Total a pagar',
    depositLabel: 'Abono inicial (30%) a pagar ahora',
    remainingLabel: 'Saldo restante',
    layawayHint: 'Tienes un mes para completar el pago.',
    fileLabel: 'Adjuntar comprobante',
    fileButton: 'Elegir imagen',
    fileButtonChange: 'Cambiar imagen',
    fileHint: 'Sube una captura o foto del comprobante (imagen). Es obligatorio para finalizar.',
    ctaPrimary: 'Listo, finalizar pedido',
    ctaSecondary: 'Cancelar',
    note: `¿Tienes problemas para adjuntar? Escríbenos por WhatsApp al ${PAYMENT_DETAILS.whatsapp}.`,
  },

  /**
   * Copy del PASO de pago con tarjeta (proveedor ONVO). Antes vivía como constantes
   * sueltas en `checkout-payment-page.component.ts`; centralizado aquí por la regla de
   * copy verbatim. Tono de marca cálido (a juego con el tagline «…más perrona…»); en
   * cobros y reembolsos prima la claridad. La mascota `charging_error.png` acompaña el
   * panel de error.
   */
  cardPayment: {
    /** Título del panel de error (fase `failed`). */
    errorTitle: 'Uy, el pago no pasó esta vez',
    /** Frase de tranquilidad cuando aún se puede reintentar el mismo intent. */
    reassureRetry:
      'Nada se perdió: tu carrito sigue intacto y bien cuidado. Puedes reintentar el pago o volver a revisarlo cuando quieras. 🐾',
    /** Frase de tranquilidad cuando ya no hay reintento (terminal). */
    reassureBack:
      'Nada se perdió: tu carrito sigue intacto y bien cuidado. Vuelve a revisarlo cuando quieras. 🐾',

    /** Tarjeta rechazada, según el `error.code` de ONVO. */
    declined:
      'Tu banco no dio el visto bueno a esta tarjeta. Revisa los datos o prueba con otra; tu carrito te espera intacto.',
    insufficientFunds:
      'A esta tarjeta no le alcanzó el saldo esta vez. Prueba con otra; tu carrito te espera intacto.',
    expiredCard: 'Esta tarjeta ya venció. Usa una vigente y seguimos; tu carrito te espera intacto.',
    incorrectCvc: 'El código de seguridad (CVV) no cuadra. Revísalo e inténtalo de nuevo.',
    processingError: 'No pudimos leer bien la tarjeta ahora mismo. Dale otra vez, por favor.',
    generic: 'No logramos procesar tu tarjeta. Revisa los datos o prueba con otra; aquí seguimos.',

    /** Fallos técnicos del flujo (no del banco). */
    createFailed: 'No pudimos arrancar el pago. Dale otra vez, por favor; tu carrito sigue intacto.',
    timeout:
      'El pago se está tomando más de lo normal. Puedes reintentar; tu carrito sigue intacto.',
    network:
      'Se nos cortó la conexión un momento. Revisa tu internet y reintenta; tu carrito sigue intacto.',
    declineFallback: 'Tu pago no se pudo procesar. Dale otra vez, por favor.',

    /** Escalada al agotar los reintentos (ofrece la vía de WhatsApp de la tienda). */
    escalation: `Llevamos varios intentos y el pago sigue sin pasar. Tranquilo: tu carrito sigue intacto y puedes volver a intentarlo cuando quieras. Si prefieres, escríbenos por WhatsApp al ${PAYMENT_DETAILS.whatsapp} y lo resolvemos juntos. 🐾`,
    /** Cobrado pero sin stock (reembolsado): terminal, sin reintento de tarjeta. */
    outOfStock:
      'Se cobró tu tarjeta, pero el producto voló justo mientras confirmábamos el pago. Ya te hemos reembolsado el cobro completo. Revisa tu carrito e inténtalo de nuevo.',
    /** Sesiones que ya no admiten volver a montar el intent. */
    sessionExpired: 'La sesión de pago venció. Vuelve al carrito para empezar un pago nuevo.',
    sessionCancelled: 'Se canceló la sesión de pago. Vuelve al carrito para empezar uno nuevo.',
    sessionRefunded: 'Este pago fue reembolsado. Revisa tu carrito antes de intentarlo de nuevo.',
  },

  /**
   * PUERTA de stock del checkout (secuestro de stock por sesión de pago, spec
   * perronstore-api/docs/specs/checkout-stock-flotante-pedidos-plan.md). El backend abre un HOLD
   * flotante al
   * «Confirmar pedido» (ambos métodos) y, sin cupo, responde 409 UNIFORME distinguido por `error`.
   * El front enruta el copy por código. `stockHeld` y `throttled` son VERBATIM (espejo de precompras);
   * `stockOut` NO promete un aviso (la promesa vive en el modal «avísame» del catálogo, POST
   * /create-alert). `timer` sirve para 5 y 15 min (el instante lo da el backend) y lleva `{mm:ss}`.
   */
  reservation: {
    /** VERBATIM (409 stock_held): espejo del rótulo de precompras. NO editar sin tocar el spec. */
    stockHeld:
      'Todas las unidades se encuentran en proceso de pago. Te invitamos a seguir intentándolo, por si se liberan unidades no pagadas en los próximos minutos',
    /** 409 stock_out: aviso SIN promesa de notificación (la promesa la cablea el modal «avísame»). */
    stockOut: 'Este producto se ha agotado.',
    /** CTA del aviso de agotado: abre el modal «avísame» del catálogo. */
    stockOutCta: 'Avísame cuando vuelva',
    /** Countdown del hold (5 o 15 min; el instante lo da el backend). Lleva `{mm:ss}`. */
    timer: 'Tienes {mm:ss} para completar tu compra',
    /** Aviso al expirar el hold (en el modal SINPE o en la página de tarjeta). */
    expired:
      'Tu reserva ha expirado. Vuelve a intentarlo: si quedan unidades libres, se reservarán de nuevo.',
    /** VERBATIM (429): rate-limiting de la puerta de reserva. NO editar sin tocar el spec. */
    throttled: 'Has hecho demasiados intentos. Espera un momento y vuelve a intentarlo.',
    /**
     * Desglose por producto (409 con `items[]`): reabrimos el cart drawer con las líneas en conflicto
     * resaltadas. `conflictFreeUnits` y `conflictAdjustCta` llevan `{n}` (unidades libres).
     */
    conflictHint: 'Algunos productos no tienen unidades suficientes ahora mismo. Ajusta las líneas marcadas para continuar.',
    /** Etiqueta por línea con las unidades libres (n > 0). Lleva `{n}`. */
    conflictFreeUnits: 'Quedan {n} libres ahora',
    /** Etiqueta por línea cuando no queda ninguna libre (agotada o toda en proceso de pago). */
    conflictNoneLeft: 'Sin unidades libres ahora mismo',
    /** CTA de auto-ajuste: deja la cantidad en las unidades libres. Lleva `{n}`. */
    conflictAdjustCta: 'Dejar {n}',
    /** CTA cuando no quedan unidades libres: eliminar la línea. */
    conflictRemoveCta: 'Eliminar',
  },

  /**
   * Modal informativo (NO bloqueante) al elegir Uber Flash / mensajero propio
   * como método de envío. Se muestra una sola vez por sesión de checkout — no
   * exige checkbox ni impide continuar, solo invita a coordinar por WhatsApp.
   */
  uberNotice: {
    title: 'Coordiná tu entrega con Uber Flash',
    /** VERBATIM — texto exacto solicitado. */
    body: 'Al seleccionar Uber Flash como método de envío, debés ponerte en contacto con nosotros para coordinar la entrega de tu pedido al mensajero de Uber.',
    whatsappLabel: 'Escribir por WhatsApp',
    /** Mismo número oficial que el resto del sitio (footer, FAB), con mensaje prellenado. */
    whatsappUrl:
      'https://wa.me/50689125220?text=Hola%2C%20eleg%C3%AD%20Uber%20Flash%20para%20mi%20pedido%20y%20quiero%20coordinar%20la%20entrega',
    cta: 'Entendido',
  },

  /** Copy de la pantalla de confirmación de pedido (`checkout-success-page`). */
  checkoutSuccess: {
    /** Solo para retiro en tienda (`delivery_method === 'ps'`). VERBATIM. */
    pickupHours:
      'Podés pasar a retirar tu pedido a partir de una hora después de la confirmación. Horario: martes a domingo, de 12:00 a 19:00.',
  },
} as const;

/**
 * Copy del carrito que deja de existir en la API (token ajeno, caducado o ya
 * convertido en pedido). ÚNICO sitio de la frase: lo reutilizan la razón
 * `cart_expired` del código promocional y los toasts de quitar línea/código.
 * Verbatim — bloqueado con test.
 */
export const CART_COPY = {
  gone: 'Tu carrito ya no está disponible. Vuelve a añadir los productos.',
} as const;

/** Sustituye el placeholder `{n}` de un copy de conflicto por el número de unidades. */
export function withUnits(meta: string, units: number): string {
  return meta.replace('{n}', String(units));
}

/**
 * Inyecta el abono inicial ya formateado en una meta con el placeholder
 * `{deposit}` (la del toggle de apartado). La interpolación del número
 * (separador de miles) se hace fuera, con `formatInteger`: el copy ya trae el
 * `₡` escrito, así que `formatCrc` aquí daría `₡₡7.500`.
 */
export function withDeposit(meta: string, depositFormatted: string): string {
  return meta.replace('{deposit}', depositFormatted);
}

/**
 * Inyecta el umbral mínimo ya formateado en el hint del toggle deshabilitado
 * (placeholder `{minSubtotal}`). Mismo molde que `withDeposit`.
 */
export function withMinSubtotal(hint: string, minSubtotalFormatted: string): string {
  return hint.replace('{minSubtotal}', minSubtotalFormatted);
}

/**
 * Copy de PRECOMPRA (Bloque 1: SINPE/transferencia con comprobante obligatorio).
 *
 * El modal de precompra ya no crea la `Prepurchase` al enviar el formulario:
 * pasa a un paso de método de pago (solo sinpe/transfer; la tarjeta llega con
 * el Bloque 3) y reutiliza el modal de comprobante del checkout
 * (`payment-data-modal`) con el split 30 %/70 % y los OVERRIDES de abajo.
 *
 * ⚠️ `paymentDataOverrides` sustituye las DOS únicas líneas del modal de
 * comprobante cuyo copy de APARTADO es FALSO para precompra (en precompra el
 * 70 % se paga al recibir el producto, no «en un mes»). El resto del copy del
 * modal (intro, labels, «A nombre de», Cancelar) se reutiliza tal cual de
 * `CHECKOUT_COPY.paymentData` (D14).
 */
export const PREPURCHASE_COPY = {
  /** CTA del formulario de precompra: pasa al paso de pago SIN crear nada. */
  ctaContinue: 'Continuar al pago',

  /**
   * Paso de método de pago. El encabezado reutiliza
   * `CHECKOUT_COPY.layawayDepositSelector.title` y las opciones
   * `CHECKOUT_COPY.paymentOptions` (misma fuente que el selector del 30 % del
   * checkout); aquí solo vive la vuelta al formulario.
   */
  paymentStep: {
    back: 'Volver',
  },

  /**
   * Reserva flotante de 15 minutos (Bloque 2). Los TRES primeros textos son
   * VERBATIM del spec (docs/specs/precompras-pago-web-plan.md → Copy verbatim)
   * y están blindados con test: NO editar sin tocar el spec.
   *
   *  - `soldOut`: rótulo que se muestra ante CUALQUIER 409 de
   *    `/prepurchase-sessions` o `/new-web-prepurchase` (cupo derivado agotado).
   *    El backend manda el mismo texto (`PrepurchaseSoldOutException`), pero el
   *    front pinta SIEMPRE esta constante (fuente única de copy, D14).
   *  - `timer`: lleva el placeholder `{mm:ss}` (p. ej. «14:59»); lo interpola
   *    el componente de cuenta atrás (`app-countdown`).
   *  - `expired`: aviso al llegar el countdown a 0.
   */
  reservation: {
    soldOut:
      'Todas las unidades se encuentran en proceso de pago. Te invitamos a seguir intentándolo, por si se liberan unidades no pagadas en los próximos minutos',
    timer: 'Tienes {mm:ss} para completar tu reserva',
    expired:
      'Tu reserva ha expirado. Vuelve a intentarlo: si quedan unidades libres, se reservarán de nuevo.',
    /** CTA del aviso de expiración/cupo: reabre una sesión nueva (nuevo countdown). */
    retryCta: 'Volver a intentarlo',
    /** Mensaje del backend cuando la sesión no existe o no coincide (422). */
    invalidSession: 'Tu reserva no es válida. Vuelve a intentarlo.',
    /**
     * VERBATIM del spec (Copy verbatim → 429): rate-limiting de
     * `POST /prepurchase-sessions`. Se pinta ante CUALQUIER 429, con el mismo
     * tratamiento que el `soldOut` (no rompe el modal). NO editar sin tocar el spec.
     */
    throttled: 'Has hecho demasiados intentos. Espera un momento y vuelve a intentarlo.',
  },

  /** Overrides del modal de comprobante en contexto precompra (ver docblock). */
  paymentDataOverrides: {
    layawayHint: 'El 70% restante se paga al recibir el producto.',
    ctaPrimary: 'Listo, finalizar precompra',
  },

  /**
   * Panel de éxito del modal de precompra. `message` lleva el placeholder
   * `{monto}` (abono del 30 % ya formateado en CRC, ver `withMonto`).
   */
  success: {
    message:
      '¡Tu precompra está reservada! Hemos recibido tu comprobante del abono inicial (30%: {monto}) y lo estamos verificando. El 70% restante se paga al recibir el producto.',
    deliveryLabel: 'Envío:',
    dueDateLabel: 'Fecha aproximada de entrega:',
    dueDateFallback: 'por confirmar',
    ctaClose: 'Cerrar',
    ctaBrowse: 'Ver otras precompras',
  },

  /** Nota bajo los totales del formulario (el pago ocurre EN el flujo, ya no por correo). */
  totalsNote:
    'Pagarás el abono inicial (30%) al continuar. El 70% restante se paga al recibir el producto.',

  /**
   * Momento en que la precompra genera Perrón Points (1 PP por cada ₡100; el
   * envío no suma). Espejo del ledger de PP del backend. Solo se muestra con
   * SESIÓN INICIADA: la precompra de invitado no acumula para el cliente
   * (D14/D19), así que el bloque se oculta a invitados para no prometer puntos
   * que no se ganan. El «cuándo» del abono del 30 % depende del método —tarjeta
   * acredita al procesarse el pago; SINPE/transferencia, al confirmar la
   * tienda—; el 70 % restante siempre al completar el pago del pedido.
   */
  perronPoints: {
    /** Método TARJETA ya fijado: página de pago ONVO y su panel de éxito. */
    card: 'El abono del 30% suma Perrón Points al procesarse tu pago. El 70% restante los suma cuando completes el pago, al recibir el pedido.',
    /** Método SINPE/TRANSFERENCIA ya fijado: modal de comprobante y éxito del modal. */
    manual:
      'El abono del 30% suma Perrón Points cuando confirmemos tu precompra. El 70% restante los suma cuando completes el pago, al recibir el pedido.',
    /** Método aún SIN fijar: totales del formulario y paso de selección de método. */
    both: 'El abono del 30% suma Perrón Points al procesarse tu pago con tarjeta, o cuando confirmemos tu precompra si pagas por SINPE o transferencia. El 70% restante los suma cuando completes el pago, al recibir el pedido.',
  },

  /**
   * Copy de la página de pago con TARJETA de la precompra (Bloque 3, ONVO). Los errores genéricos de
   * tarjeta (declinada, saldo, CVV, red…) se reutilizan de `CHECKOUT_COPY.cardPayment`; aquí solo
   * viven los textos ESPECÍFICOS de precompra que difieren del checkout de carrito.
   *
   * `success` es VERBATIM del spec (variante tarjeta del correo `ConfirmPrepurchase`): lleva el
   * placeholder `{monto}` (abono 30 % formateado en CRC, ver `withMonto`). NO editar sin tocar el spec.
   * `sessionExpired` reutiliza el verbatim de expiración de la reserva (`reservation.expired`).
   */
  cardPayment: {
    /** Encabezado del paso de pago (la reserva de 15 min corre en paralelo). */
    payTitle: 'Pagar el abono con tarjeta',
    /** Etiqueta del importe a cobrar ahora (30 %). */
    amountLabel: 'Abono inicial (30%) a pagar ahora',
    /** Cobrado pero la reserva se agotó al confirmar (reembolsado): terminal. Variante precompra del 409. */
    outOfStock:
      'Se cobró tu tarjeta, pero las unidades libres se agotaron mientras confirmábamos el pago. Ya te hemos reembolsado el cobro completo. Vuelve a intentarlo: si quedan unidades libres, se reservarán de nuevo.',
    /** Reembolso solicitado pero pendiente de confirmación por ONVO. */
    refundPending:
      'Se cobró tu tarjeta, pero no pudimos confirmar la precompra. Ya solicitamos la devolución del cobro; está pendiente de confirmación. Te avisaremos en cuanto se complete.',
    /** Reembolso automático fallido: atención manual. */
    refundFailed:
      'Se cobró tu tarjeta, pero no pudimos confirmar la precompra ni completar la devolución automática. Nuestro equipo ya lo está revisando; escríbenos por WhatsApp si no ves el reembolso.',
    /** Título del panel de éxito. */
    successTitle: '¡Precompra confirmada!',
    /** VERBATIM (variante tarjeta del correo, spec → Copy verbatim). `{monto}` = abono 30 %. */
    success:
      '¡Tu precompra está confirmada! Hemos cobrado el abono inicial (30%: {monto}) a tu tarjeta. El 70% restante se paga al recibir el producto.',
    /** CTA del panel de éxito: seguir explorando precompras. */
    successBrowse: 'Ver otras precompras',
    /** CTA del panel de éxito/errores: volver a la tienda. */
    backToStore: 'Volver a la tienda',

    /**
     * Panel «confirmando» (fase `pending`), addenda «Cobro hecho con la reserva vencida» (D81). Dos
     * variantes: `confirmingReceived*` con el cobro CONSTATADO (`approved` o 409 `cancelled_recovering`);
     * `confirmingInFlight*` con el pago en vuelo (3DS, 422 del widget, red sin cobro constatado). Nunca
     * «Recibimos tu pago» sin cobro constatado.
     */
    confirmingInFlightTitle: 'Tu pago está en proceso',
    confirmingInFlightBody:
      'Estamos confirmando tu pago. En cuanto se acredite, tu precompra quedará reservada. Puedes comprobar el estado ahora o volver más tarde.',
    confirmingReceivedTitle: 'Recibimos tu pago',
    confirmingReceivedBody:
      'Recibimos tu pago y lo estamos confirmando. En unos minutos verás aquí si tu precompra quedó reservada; si no pudiéramos confirmarla, te devolveremos el cobro completo. No hace falta que vuelvas a pagar.',
    /** Botón del panel «confirmando». */
    checkStatusCta: 'Comprobar estado',
    /** Mensajes de «Comprobar estado» (`checkSession`). */
    stillConfirming: 'Aún estamos confirmando tu pago. Vuelve a comprobar en unos minutos.',
    checkFailed: 'No pudimos comprobar el estado ahora. Revisa tu conexión e inténtalo de nuevo.',
    /** El poller agotó sus 30 ticks (5 min) sin un desenlace. */
    pollExhausted: `Está tardando más de lo normal. Pulsa «Comprobar estado» dentro de unos minutos o escríbenos por WhatsApp al ${PAYMENT_DETAILS.whatsapp} y lo revisamos contigo. No vuelvas a pagar mientras tanto.`,
    /** 409 `charge_not_eligible` con `reason=refunded`: terminal, sin reintento de tarjeta. */
    chargeRefunded:
      'Este cobro ya se devolvió a tu tarjeta, así que tu precompra no quedó confirmada. Según tu banco, puede tardar unos días en verse. Si quieres, vuelve a intentarlo: si quedan unidades libres, se reservarán de nuevo.',
    /** 409 neutro (`charge_not_eligible` sin `reason`/desconocido, u otro 409): terminal, sin reintento. */
    chargeUnknown: `No pudimos confirmar el estado de tu pago. Escríbenos por WhatsApp al ${PAYMENT_DETAILS.whatsapp} con el correo que usaste en la precompra y lo revisamos. No vuelvas a pagar mientras tanto.`,
    /** Reserva `expired` + intent local `pending` + 422 de `/complete`: terminal, sin poller (D75). */
    expiredMaybePaid:
      'Tu reserva ha expirado. Si llegaste a completar el pago, no vuelvas a pagar: en unos minutos recibirás por correo la confirmación de tu precompra o te devolveremos el cobro automáticamente. Si no llegaste a pagar, vuelve a intentarlo: si quedan unidades libres, se reservarán de nuevo.',
  },
} as const;

/**
 * Inyecta el abono del 30 % ya formateado (con `formatCRC`, símbolo incluido)
 * en un texto con el placeholder `{monto}` (el mensaje de éxito de precompra).
 * Mismo patrón que `withDeposit`.
 */
export function withMonto(text: string, montoFormatted: string): string {
  return text.replace('{monto}', montoFormatted);
}
